import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import '../styles/components/radiobutton.css';

const nonTranslateClassName = 'notranslate';

const restrictedPages = [
  '/ai-nutrition-vitamins-minerals',
  '/my-nutrition-status',
  '/ai-assistant',
  '/home',
  '/account/billing',
  '/delete-account',
  '/checkout',
  '/checkout/success',
  '/sign-in',
];

const languageCodeMapping = {
  'en-us': 'en',
  'en-gb': 'en',
  'fr-ca': 'fr',
  'en-ca': 'en',
  'pt-br': 'pt',
  nb: 'no',
  'zh-cn': 'zh-CN',
  'zh-tw': 'zh-TW',
};

const languages = [
  { code: 'en-us', countryCode: 'us', label: 'Eng', fullName: 'English (USA)' },
  { code: 'en-gb', countryCode: 'gb', label: 'Eng', fullName: 'English (UK)' },
  { code: 'sv', countryCode: 'se', label: 'Sve', fullName: 'Swedish' },
  { code: 'da', countryCode: 'dk', label: 'Dan', fullName: 'Danish' },
  { code: 'nb', countryCode: 'no', label: 'Nor', fullName: 'Norwegian' },
  { code: 'fi', countryCode: 'fi', label: 'Fin', fullName: 'Finnish' },
  { code: 'is', countryCode: 'is', label: 'Ice', fullName: 'Icelandic' },
  { code: 'et', countryCode: 'ee', label: 'Est', fullName: 'Estonian' },
  { code: 'lv', countryCode: 'lv', label: 'Lav', fullName: 'Latvian' },
  { code: 'lt', countryCode: 'lt', label: 'Lit', fullName: 'Lithuanian' },
  { code: 'es', countryCode: 'es', label: 'Esp', fullName: 'Spanish' },
  { code: 'fr', countryCode: 'fr', label: 'Fra', fullName: 'French (France)' },
  { code: 'fr-ca', countryCode: 'ca', label: 'Fra', fullName: 'French (Canada)' },
  { code: 'en-ca', countryCode: 'ca', label: 'Eng', fullName: 'English (Canada)' },
  { code: 'de', countryCode: 'de', label: 'Deu', fullName: 'German' },
  { code: 'it', countryCode: 'it', label: 'Ita', fullName: 'Italian' },
  { code: 'nl', countryCode: 'nl', label: 'Nld', fullName: 'Dutch' },
  { code: 'pl', countryCode: 'pl', label: 'Pol', fullName: 'Polish' },
  { code: 'ar', countryCode: 'ae', label: 'Ara', fullName: 'Arabic' },
  { code: 'hi', countryCode: 'in', label: 'Hin', fullName: 'Hindi' },
  { code: 'pt', countryCode: 'pt', label: 'Por', fullName: 'Portuguese (Portugal)' },
  { code: 'pt-br', countryCode: 'br', label: 'Por', fullName: 'Portuguese (Brazil)' },
  { code: 'zh-cn', countryCode: 'cn', label: 'Chi', fullName: 'Chinese (Simplified)' },
  { code: 'ja', countryCode: 'jp', label: 'Jap', fullName: 'Japanese' },
  { code: 'ko', countryCode: 'kr', label: 'Kor', fullName: 'Korean' },
];

const defaultLanguageCode = 'en-us';
const sessionPreferenceKey = 'phn:languagePreference';

const getFlagUrl = (countryCode) => {
  if (!countryCode) return '';
  return `https://flagcdn.com/24x18/${countryCode}.png`;
};

class LanguageCatalog {
  constructor(languageList, fallbackLanguageCode, mapping, pinnedMenuOrder) {
    this.languageList = languageList;
    this.fallbackLanguageCode = fallbackLanguageCode;
    this.mapping = mapping;
    this.pinnedMenuOrder = pinnedMenuOrder;

    this.sortedLanguageList = this.buildMenuLanguageList(this.pinnedMenuOrder);

    this.supportedGoogleLanguageCodes = Array.from(
      new Set(
        languageList.map((language) => {
          const mappedCode = mapping[language.code];
          if (mappedCode) return mappedCode;
          return language.code.toLowerCase().split('-')[0];
        })
      )
    ).join(',');
  }

  normalizeCode(code) {
    return String(code || '').trim().toLowerCase();
  }

  findByCode(code) {
    const normalized = this.normalizeCode(code);
    return this.languageList.find((l) => this.normalizeCode(l.code) === normalized) || null;
  }

  buildMenuLanguageList(pinnedCodes) {
    const pinnedNormalized = pinnedCodes.map(this.normalizeCode);
    const pinnedItems = pinnedNormalized.map((c) => this.findByCode(c)).filter(Boolean);
    const pinnedSet = new Set(pinnedNormalized);

    const remaining = this.languageList
      .filter((l) => !pinnedSet.has(this.normalizeCode(l.code)))
      .sort((a, b) => a.fullName.localeCompare(b.fullName));

    return [...pinnedItems, ...remaining];
  }

  getSortedLanguages() {
    return this.sortedLanguageList;
  }

  getLanguageOrFallback(languageCode) {
    const match = this.findByCode(languageCode);
    if (match) return match;

    const fallback = this.findByCode(this.fallbackLanguageCode);
    return fallback || this.languageList.find((l) => l.code === 'en-us');
  }

  toGoogleLanguageCode(languageCode) {
    const match = this.getLanguageOrFallback(languageCode);
    const mapped = this.mapping[match.code];
    if (mapped) return mapped;
    return match.code.split('-')[0];
  }
}

class LanguagePreferenceSessionStore {
  constructor(storageKey, catalog) {
    this.storageKey = storageKey;
    this.catalog = catalog;
  }

  readPreference() {
    try {
      const val = sessionStorage.getItem(this.storageKey);
      if (this.catalog.findByCode(val)) return val;
    } catch (e) {}
    return null;
  }

  writePreference(code) {
    try {
      if (this.catalog.findByCode(code)) {
        sessionStorage.setItem(this.storageKey, code);
      }
    } catch (e) {}
  }
}

class GoogleTranslateController {
  constructor(catalog) {
    this.catalog = catalog;
    this.pageLanguage = 'en';
  }

  clearCookie() {
    const domain = window.location.hostname.includes('.')
      ? `domain=.${window.location.hostname.split('.').slice(-2).join('.')}`
      : '';

    document.cookie = `googtrans=; path=/; expires=Thu, 01 Jan 1970 00:00:01 GMT; SameSite=Lax; ${domain}`;
    document.cookie = `googtrans=; path=/; expires=Thu, 01 Jan 1970 00:00:01 GMT; SameSite=Lax;`;
  }

  setCookie(languageCode) {
    const googleCode = this.catalog.toGoogleLanguageCode(languageCode);
    const cookieVal = `googtrans=/en/${googleCode}`;
    const domain = window.location.hostname.includes('.')
      ? `domain=.${window.location.hostname.split('.').slice(-2).join('.')}`
      : '';

    document.cookie = `${cookieVal}; path=/; SameSite=Lax; ${domain}`;
    document.cookie = `${cookieVal}; path=/; SameSite=Lax;`;
  }

  resetWidgetToPageLanguage(requestId, requestIdRef) {
    let attempts = 0;

    const tryReset = () => {
      if (requestId !== requestIdRef.current) return;

      const select = document.querySelector('.goog-te-combo');
      if (!select) {
        if (attempts++ < 30) setTimeout(tryReset, 100);
        return;
      }

      if (select.value !== this.pageLanguage) {
        select.value = this.pageLanguage;
      }
    };

    tryReset();
  }

  applyToWidget(languageCode, requestId, requestIdRef) {
    const googleCode = this.catalog.toGoogleLanguageCode(languageCode);
    let attempts = 0;

    const tryApply = () => {
      if (requestId !== requestIdRef.current) return;

      const select = document.querySelector('.goog-te-combo');
      if (!select) {
        if (attempts++ < 30) setTimeout(tryApply, 100);
        return;
      }

      if (select.value !== googleCode) {
        select.value = googleCode;
        select.dispatchEvent(new Event('change'));
      }
    };

    tryApply();
  }
}

const LanguageSelector = () => {
  const location = useLocation();
  const menuRef = useRef(null);
  const applyRequestIdRef = useRef(0);

  const catalog = useMemo(
    () => new LanguageCatalog(languages, defaultLanguageCode, languageCodeMapping, ['en-us', 'ar']),
    []
  );
  const sessionStore = useMemo(() => new LanguagePreferenceSessionStore(sessionPreferenceKey, catalog), [catalog]);
  const translateController = useMemo(() => new GoogleTranslateController(catalog), [catalog]);

  const [selectedLanguage, setSelectedLanguage] = useState(defaultLanguageCode);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);

  const getEffectiveLanguage = useCallback(
    (path) => {
      const isRestricted = restrictedPages.includes(path);
      if (isRestricted) return defaultLanguageCode;

      const pref = sessionStore.readPreference();
      return pref || defaultLanguageCode;
    },
    [sessionStore]
  );

  const applyLanguage = useCallback(
    (code) => {
      applyRequestIdRef.current += 1;
      const requestId = applyRequestIdRef.current;

      const googleCode = catalog.toGoogleLanguageCode(code);

      if (googleCode === 'en') {
        translateController.clearCookie();
        translateController.resetWidgetToPageLanguage(requestId, applyRequestIdRef);
        return;
      }

      translateController.setCookie(code);
      translateController.applyToWidget(code, requestId, applyRequestIdRef);
    },
    [translateController, catalog]
  );

  useEffect(() => {
    const isRestricted = restrictedPages.includes(location.pathname);
    const hasTranslationCookie = document.cookie
      .split(';')
      .some((item) => item.trim().startsWith('googtrans='));

    if (isRestricted && hasTranslationCookie) {
      translateController.clearCookie();
      window.location.reload();
      return;
    }

    const effective = getEffectiveLanguage(location.pathname);
    setSelectedLanguage(effective);

    if (!window.googleTranslateElementInit) {
      window.googleTranslateElementInit = () => {
        new window.google.translate.TranslateElement(
          {
            pageLanguage: 'en',
            includedLanguages: catalog.supportedGoogleLanguageCodes,
            autoDisplay: false,
          },
          'google_translate_element'
        );
        setIsLoaded(true);
      };

      const script = document.createElement('script');
      script.src = '//translate.google.com/translate_a/element.js?cb=googleTranslateElementInit';
      document.body.appendChild(script);
    } else {
      setIsLoaded(true);
    }
  }, [catalog.supportedGoogleLanguageCodes, getEffectiveLanguage, location.pathname, translateController]);

  useEffect(() => {
    if (isLoaded) {
      applyLanguage(selectedLanguage);
    }
  }, [selectedLanguage, isLoaded, applyLanguage]);

  const handleLanguageChange = (code) => {
    const isRestricted = restrictedPages.includes(location.pathname);
    if (isRestricted) return;

    if (code === selectedLanguage) {
      setIsMenuOpen(false);
      return;
    }

    const googleCode = catalog.toGoogleLanguageCode(code);

    if (googleCode === 'en') {
      sessionStore.writePreference(code);
      translateController.clearCookie();
      applyRequestIdRef.current += 1;
      translateController.resetWidgetToPageLanguage(applyRequestIdRef.current, applyRequestIdRef);
      window.location.reload();
      return;
    }

    sessionStore.writePreference(code);
    setSelectedLanguage(code);
    setIsMenuOpen(false);
  };

  const currentLangObj = catalog.getLanguageOrFallback(selectedLanguage);
  const isRestrictedRoute = restrictedPages.includes(location.pathname);

  return (
    <div className={`language-selector-container ${nonTranslateClassName}`} ref={menuRef}>
      <div id="google_translate_element" style={{ display: 'none' }} />

      <button
        className="language-button"
        onClick={() => setIsMenuOpen(!isMenuOpen)}
        disabled={isRestrictedRoute}
        aria-label="Select language"
      >
        <span className="language-flag" style={{ backgroundImage: `url(${getFlagUrl(currentLangObj.countryCode)})` }} />
        <span className="language-abbr">{currentLangObj.label}</span>
      </button>

      {isMenuOpen && (
        <div className="language-menu">
          {catalog.getSortedLanguages().map((lang) => (
            <button
              key={lang.code}
              className={`language-menu-item ${lang.code === selectedLanguage ? 'active' : ''}`}
              onClick={() => handleLanguageChange(lang.code)}
            >
              <span className="language-flag" style={{ backgroundImage: `url(${getFlagUrl(lang.countryCode)})` }} />
              <span className="language-name">{lang.fullName}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default LanguageSelector;
